import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageContainer from '@/components/layout/PageContainer';
import type { KnowledgeCategory, KnowledgeArticle } from '@/types/knowledgeBook';

type Props = {
  category: KnowledgeCategory | undefined;
  article: KnowledgeArticle | null;
  loading: boolean;
  onBackToCategory: () => void;
  onBackToHome: () => void;
};

export default function ArticleReaderView({ category, article, loading, onBackToCategory, onBackToHome }: Props) {
  const { t } = useTranslation();
  if (!category) return null;

  if (loading || !article) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-primary" />
      </div>
    );
  }

  const paragraphs = article.content.split('\n\n');

  return (
    <>
      {/* Breadcrumb */}
      <nav className="mb-6 flex items-center gap-1.5 text-sm text-muted-foreground">
        <Button variant="link" size="sm" onClick={onBackToHome} className="h-auto p-0 text-muted-foreground hover:text-foreground">
          {t('knowledge.title')}
        </Button>
        <ChevronRight className="size-3" />
        <Button variant="link" size="sm" onClick={onBackToCategory} className="h-auto p-0 text-muted-foreground hover:text-foreground">
          {category.title}
        </Button>
        <ChevronRight className="size-3" />
        <span className="text-foreground truncate">{article.title}</span>
      </nav>

      <PageContainer variant="reading" as="article">
        {/* Header */}
        <div className="mb-8">
          <div className="mb-3 flex items-center gap-2">
            <span
              className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
              style={{ backgroundColor: `${category.color}18`, color: category.color }}
            >
              {category.title}
            </span>
          </div>
          <h1 className="text-3xl font-heading text-foreground leading-tight">{article.title}</h1>
          <p className="mt-3 text-lg text-muted-foreground leading-relaxed">{article.preview}</p>
        </div>

        {/* Content */}
        <div className="space-y-4">
          {paragraphs.map((para, i) => {
            if (para.startsWith('**') && para.includes('**\n')) {
              const [header, ...rest] = para.split('\n');
              const headerText = header.replace(/\*\*/g, '');
              return (
                <div key={i} className="mt-6">
                  <h3 className="mb-1 text-lg font-bold text-foreground">{headerText}</h3>
                  <p className="text-base leading-relaxed text-muted-foreground">{rest.join('\n')}</p>
                </div>
              );
            }
            if (/^\*\*[^*]+\*\*$/.test(para.trim())) {
              return (
                <h2 key={i} className="mt-8 mb-2 text-xl font-bold text-foreground">
                  {para.replace(/\*\*/g, '')}
                </h2>
              );
            }
            return (
              <p key={i} className="text-base leading-relaxed text-muted-foreground">
                {renderInlineBold(para)}
              </p>
            );
          })}
        </div>

        {/* Back */}
        <div className="mt-10 border-t border-border pt-6">
          <Button variant="ghost" size="sm" onClick={onBackToCategory} className="gap-1.5 text-muted-foreground">
            <ArrowLeft className="size-4" />
            {t('knowledge.backToCategory', { category: category.title })}
          </Button>
        </div>
      </PageContainer>
    </>
  );
}

function renderInlineBold(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}
